import { useQuery } from "@apollo/client";
import { BaseTable, IBaseTableProps } from "@sede-x/shell-ds-react-framework";
import { getPaginationRowModel } from "@tanstack/react-table";
import dayjs from "dayjs";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TMovementTicketRevision } from "ticketing/ticketing.types";
import { equalsIgnoreCase } from "ticketing/utils";

type Response = GqlResponse<TMovementTicketRevision[], "ticketsOfMovementAtRevision">;
const GET_TICKET_REVISIONS_OF_MOVEMENT = loader(
  "../../ticketing-graphql/ticketsOfMovementAtRevision.graphql"
);

const transformData = (tickets: TMovementTicketRevision[]) => {
  return tickets
    ?.filter(t => !equalsIgnoreCase(t?.linkStatus, "UNLINKED"))
    .map(ticket => ({
      ...ticket,
      lastModifiedDate: new Date(ticket?.lastLinkModifiedDate + "Z")
    }))
    .sort((t1, t2) => t2.lastModifiedDate.getTime() - t1.lastModifiedDate.getTime());
};

type RevisionsMovementTicketsProps = {
  movementId: string;
  revisionNumber: string;
};

const RevisionsMovementTickets = ({
  movementId,
  revisionNumber
}: RevisionsMovementTicketsProps) => {
  const { loading, error } = useQuery<Response>(GET_TICKET_REVISIONS_OF_MOVEMENT, {
    fetchPolicy: "no-cache",
    variables: { movementId, revisionNumber },
    onCompleted: data => setTickets(transformData(data.ticketsOfMovementAtRevision))
  });
  const [tickets, setTickets] = useState<TMovementTicketRevision[]>([]);

  const columns: IBaseTableProps<TMovementTicketRevision>["columns"] = [
    {
      header: "MACk Id",
      accessorKey: "id",
      size: 96
    },
    {
      header: "Ticket Number",
      accessorKey: "ticketNumber",
      size: 120
    },
    {
      header: "Actualized Volume",
      accessorKey: "actualizedVolume",
      size: 180
    },
    {
      header: "Updated By",
      accessorKey: "lastLinkModifiedBy",
      size: 120
    },
    {
      header: "Last Update Time",
      accessorKey: "lastModifiedDate",
      size: 120,
      cell: ({ row }) => dayjs(row.original.lastLinkModifiedDate).format("MM/DD/YY HH:mm:ss") // Format the date
    }
  ];
  return (
    <>
      {loading && <LoadingPanel />}
      {error && <ApolloErrorViewer error={error} />}

      <BaseTable
        data={tickets}
        columns={columns}
        tableOptions={{
          defaultColumn: {
            minSize: 100,
            maxSize: 400
          },
          enableColumnResizing: true,
          columnResizeMode: "onChange",
          getPaginationRowModel: getPaginationRowModel()
        }}
        style={{ width: "100%", tableLayout: "fixed" }}
      />
    </>
  );
};

export default RevisionsMovementTickets;
